import type { MaterialNodeSpec } from '../../graph/domain/types';
import type { WorkflowGraph } from '../../graph';
import type { Store } from '../types';
import { generateGuid } from '../../graph/utils/id';
import { withSyncGuard } from '../sync/syncFlag';
import { computeInitialComposition } from '../compositionService';

type SetState = (partial: Partial<Store> | ((state: Store) => Partial<Store>)) => void;
type GetState = () => Store;

export function createMaterialActions(
  set: SetState,
  get: GetState,
  getGraph: () => WorkflowGraph | null | undefined,
) {
  return {
    addMaterialNode(spec: MaterialNodeSpec): string {
      const id = spec.id ?? generateGuid();
      const fullSpec: MaterialNodeSpec = {
        ...spec,
        id,
        vertexId: spec.vertexId ?? id,
        // A material added without derivation info is an entry-point (root):
        // anchor it so lineage traversal has a base case. Post-state materials
        // pass their own composition and are left untouched.
        composition: spec.composition ?? computeInitialComposition(id, spec.name),
        createdAt: spec.createdAt ?? new Date().toISOString(),
      };

      set((state) => ({
        graphData: {
          ...state.graphData,
          materialNodes: { ...state.graphData.materialNodes, [id]: fullSpec },
        },
        ui: { ...state.ui, isDirty: true },
      }));

      withSyncGuard(set, () => {
        getGraph()?.addMaterialNode(fullSpec);
      });

      return id;
    },

    updateMaterialNode(id: string, patch: Partial<MaterialNodeSpec>): void {
      const current = get().graphData.materialNodes[id];
      if (!current) throw new Error(`MaterialNode not found: ${id}`);

      const updated = { ...current, ...patch };

      set((state) => ({
        graphData: {
          ...state.graphData,
          materialNodes: { ...state.graphData.materialNodes, [id]: updated },
        },
        ui: { ...state.ui, isDirty: true },
      }));

      withSyncGuard(set, () => {
        getGraph()?.updateMaterialNode(id, patch);
      });
    },

    removeMaterialNode(id: string): void {
      set((state) => {
        const { [id]: _, ...rest } = state.graphData.materialNodes;
        // Also remove edges connected to this material
        const flowEdges: Record<string, typeof state.graphData.flowEdges[string]> = {};
        for (const [eid, edge] of Object.entries(state.graphData.flowEdges)) {
          if (edge.sourceId !== id && edge.targetId !== id) {
            flowEdges[eid] = edge;
          }
        }
        return {
          graphData: { ...state.graphData, materialNodes: rest, flowEdges },
          ui: { ...state.ui, isDirty: true },
        };
      });

      withSyncGuard(set, () => {
        getGraph()?.removeMaterialNode(id);
      });
    },
  };
}
